/**
 * ContractOutputEditor.js
 *
 * Output authoring editor for generated contract packages.
 * Shows document as one long scrollable page stack (like DocuSign).
 *
 * Works on transient output_editor_state:
 * - Clean template PDF backgrounds (no field overlays/artifacts)
 * - Editable overlay objects (text fields, freeform, checkboxes)
 * - All pages displayed continuously, not one-at-a-time
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as pdfjsLib from 'pdfjs-dist';
import ContractOutputEditorRightPanel from './ContractOutputEditorRightPanel';
import OutputPageThumbnails from './OutputPageThumbnails';
import { COLORS, TYPOGRAPHY, SPACING, RADIUS, SHADOWS } from '../designSystem';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const DEFAULT_PAGE_HEIGHT = 792;

/**
 * Transform field display value for contract-specific contexts.
 * Handles frequency wording and numeric formatting.
 * Note: Backend applies most formatting, this is a fallback for edge cases.
 */
function transformFieldDisplayValue(fieldKey, value) {
  if (!value) return value;

  // Transform payment_frequency for contract contexts
  if (fieldKey === 'payment_frequency') {
    if (value.toLowerCase() === 'daily') return 'Day';
    if (value.toLowerCase() === 'weekly') return 'Week';
  }

  // Currency fields - format if not already formatted
  const currencyFields = [
    'purchase_price', 'receivables_purchased', 'net_funds_provided',
    'total_payback', 'initial_estimated_payment'
  ];
  if (currencyFields.includes(fieldKey) && typeof value === 'string' && !value.startsWith('$')) {
    try {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    } catch (e) {
      // Fall through if formatting fails
    }
  }

  return value;
}

export default function ContractOutputEditor() {
  const { dealId, exportId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token') || '';

  // Editor state
  const [outputEditorState, setOutputEditorState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [previewPDF, setPreviewPDF] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // UI state - restore zoom from localStorage
  const [zoom, setZoom] = useState(() => {
    const saved = localStorage.getItem('editor_zoom');
    return saved ? parseFloat(saved) : 1;
  });
  const [selectedObjectId, setSelectedObjectId] = useState(null);

  // Drag/resize state
  const [draggedObjectId, setDraggedObjectId] = useState(null);
  const [resizingObjectId, setResizingObjectId] = useState(null);

  // Rendering state
  const [pdfDocs, setPdfDocs] = useState({});
  const [pageHeights, setPageHeights] = useState({});

  // Scroll tracking
  const containerRef = useRef(null);
  const pageRefsMap = useRef({});  // Map of page_id -> { canvasRef, overlayCanvasRef }
  const dragStateRef = useRef(null);
  const isRenderingRef = useRef(false);

  // Persist zoom to localStorage
  useEffect(() => {
    localStorage.setItem('editor_zoom', zoom.toString());
  }, [zoom]);

  // Load output editor state on mount
  useEffect(() => {
    const loadEditorState = async () => {
      try {
        if (exportId) {
          const response = await axios.get(
            `${API_URL}/contract-exports/${exportId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setOutputEditorState(response.data.edited_output_snapshot || {});
        } else if (dealId) {
          const response = await axios.post(
            `${API_URL}/deals/${dealId}/generate-contract`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setOutputEditorState(response.data.output_editor_state || {});
        }
        setLoading(false);
      } catch (err) {
        setError(`Failed to load contract: ${err.message}`);
        setLoading(false);
      }
    };

    loadEditorState();
  }, [dealId, exportId, token]);

  // Load and parse PDF backgrounds from hex strings
  useEffect(() => {
    if (!outputEditorState?.pages) return;

    const loadPDFs = async () => {
      try {
        const docs = {};
        const heights = {};

        for (const page of outputEditorState.pages) {
          if (!page.background_pdf_hex) continue;

          try {
            const hexString = page.background_pdf_hex;
            const bytes = new Uint8Array(
              hexString.match(/.{1,2}/g).map(byte => parseInt(byte, 16))
            );
            const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
            docs[page.page_id] = pdf;
            heights[page.page_id] = page.page_height || DEFAULT_PAGE_HEIGHT;
          } catch (e) {
            console.error(`Failed to load PDF for page ${page.page_id}:`, e);
          }
        }

        setPdfDocs(docs);
        setPageHeights(heights);
      } catch (err) {
        console.error('Failed to load PDFs:', err);
      }
    };

    loadPDFs();
  }, [outputEditorState?.pages]);

  // Render ALL pages to canvas (continuous scroll)
  useEffect(() => {
    if (!outputEditorState?.pages || !pdfDocs || Object.keys(pdfDocs).length === 0 || isRenderingRef.current) return;

    isRenderingRef.current = true;

    const renderAllPages = async () => {
      try {
        for (const pageData of outputEditorState.pages) {
          const pageId = pageData.page_id;
          const refs = pageRefsMap.current[pageId];

          if (!refs || !refs.canvasRef) {
            console.warn(`Canvas ref missing for page ${pageId}`);
            continue;
          }

          const pdfDoc = pdfDocs[pageId];
          if (!pdfDoc) {
            console.warn(`PDF doc missing for page ${pageId}`);
            continue;
          }

          try {
            // PRIORITY 1 FIX: Each background_pdf_hex now contains a single page, so always get page 1
            // (pdf.js uses 1-based page numbering; page 1 is the first/only page in these single-page PDFs)
            const pageNum = pdfDoc.numPages > 0 ? 1 : undefined;
            if (!pageNum) {
              console.warn(`PDF for page ${pageId} has no pages`);
              continue;
            }
            const page = await pdfDoc.getPage(pageNum);
            const vp = page.getViewport({ scale: zoom });

            // Render PDF to main canvas
            const canvas = refs.canvasRef;
            canvas.width = vp.width;
            canvas.height = vp.height;
            const ctx = canvas.getContext('2d');
            await page.render({ canvasContext: ctx, viewport: vp }).promise;

            // Render overlay with objects
            const overlayCanvas = refs.overlayCanvasRef;
            if (overlayCanvas) {
              overlayCanvas.width = vp.width;
              overlayCanvas.height = vp.height;

              const pageHeight = pageHeights[pageId] || DEFAULT_PAGE_HEIGHT;
              const pageObjects = outputEditorState.output_objects.filter(obj => obj.page_id === pageId);

              const octx = overlayCanvas.getContext('2d');
              octx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);

              pageObjects.forEach(obj => {
                const sx = obj.x * zoom;
                const sy = (pageHeight - (obj.y + obj.height)) * zoom;
                const sw = obj.width * zoom;
                const sh = obj.height * zoom;

                // Draw object box
                octx.fillStyle = 'rgba(100, 150, 255, 0.1)';
                octx.fillRect(sx, sy, sw, sh);

                // Draw border
                octx.strokeStyle = selectedObjectId === obj.object_id ? '#2563eb' : '#9ca3af';
                octx.lineWidth = selectedObjectId === obj.object_id ? 2 : 1;
                octx.strokeRect(sx, sy, sw, sh);

                // Draw checkbox or text field
                if (obj.object_type === 'checkbox') {
                  // Draw checkbox visual
                  const checkboxSize = Math.min(sw, sh);
                  const cbx = sx + (sw - checkboxSize) / 2;
                  const cby = sy + (sh - checkboxSize) / 2;

                  // Checkbox border
                  octx.strokeStyle = '#1f2937';
                  octx.lineWidth = 1;
                  octx.strokeRect(cbx + 2, cby + 2, checkboxSize - 4, checkboxSize - 4);

                  // Draw checkmark if checked
                  if (obj.checked) {
                    octx.strokeStyle = '#1f2937';
                    octx.lineWidth = 2;
                    octx.beginPath();
                    const inset = checkboxSize / 4;
                    octx.moveTo(cbx + inset, cby + checkboxSize / 2);
                    octx.lineTo(cbx + checkboxSize / 2 - inset, cby + checkboxSize - inset);
                    octx.lineTo(cbx + checkboxSize - inset, cby + inset);
                    octx.stroke();
                  }
                } else {
                  // Draw text field with field transformation
                  octx.fillStyle = '#1f2937';
                  octx.font = '11px Arial';
                  const transformedValue = transformFieldDisplayValue(obj.field_key, obj.value);
                  const fullText = transformedValue ? String(transformedValue) : obj.label || obj.object_type;

                  // Calculate how much text can fit in the field width
                  const maxTextWidth = sw - 8; // 4px padding on each side
                  const textMetrics = octx.measureText(fullText);

                  // If text fits in one line, display it
                  if (textMetrics.width <= maxTextWidth) {
                    octx.fillText(fullText, sx + 4, sy + 14);
                  } else {
                    // Text is too long, wrap to multiple lines
                    const lines = [];
                    let currentLine = '';
                    for (let i = 0; i < fullText.length; i++) {
                      const testLine = currentLine + fullText[i];
                      if (octx.measureText(testLine).width > maxTextWidth) {
                        if (currentLine) lines.push(currentLine);
                        currentLine = fullText[i];
                      } else {
                        currentLine = testLine;
                      }
                    }
                    if (currentLine) lines.push(currentLine);

                    // Draw up to 3 lines of text
                    const lineHeight = 12;
                    for (let i = 0; i < Math.min(lines.length, 3); i++) {
                      octx.fillText(lines[i], sx + 4, sy + 4 + (i + 1) * lineHeight);
                    }

                    // Add ellipsis if there are more lines
                    if (lines.length > 3) {
                      octx.fillText('...', sx + 4, sy + 4 + 4 * lineHeight);
                    }
                  }
                }

                // Draw resize handle if selected
                if (selectedObjectId === obj.object_id) {
                  const handleSize = 8;
                  octx.fillStyle = '#2563eb';
                  octx.fillRect(sx + sw - handleSize, sy + sh - handleSize, handleSize, handleSize);
                }
              });
            }
          } catch (e) {
            console.error(`Failed to render page ${pageId}:`, e);
          }
        }
      } finally {
        isRenderingRef.current = false;
      }
    };

    renderAllPages();
  }, [outputEditorState?.pages, outputEditorState?.output_objects, pdfDocs, zoom, selectedObjectId, pageHeights]);

  // Update an object in state
  const updateObject = useCallback((objectId, updates) => {
    setOutputEditorState(prev => ({
      ...prev,
      output_objects: prev.output_objects.map(obj =>
        obj.object_id === objectId ? { ...obj, ...updates } : obj
      ),
    }));
  }, []);

  // Delete an object
  const deleteObject = useCallback((objectId) => {
    setOutputEditorState(prev => ({
      ...prev,
      output_objects: prev.output_objects.filter(obj => obj.object_id !== objectId),
    }));
    setSelectedObjectId(null);
  }, []);

  // Duplicate an object
  const duplicateObject = useCallback((objectId) => {
    const obj = outputEditorState?.output_objects.find(o => o.object_id === objectId);
    if (!obj) return;

    const newId = `obj_${Math.random().toString(36).substr(2, 9)}`;
    const duplicate = { ...obj, object_id: newId, x: obj.x + 20, y: obj.y + 20 };

    setOutputEditorState(prev => ({
      ...prev,
      output_objects: [...(prev?.output_objects || []), duplicate],
    }));
    setSelectedObjectId(newId);
  }, [outputEditorState]);

  // Add freeform text
  const addFreeformText = useCallback((text) => {
    if (!outputEditorState?.pages || outputEditorState.pages.length === 0) {
      console.log('[addFreeformText] FAILED - no pages in state');
      return;
    }

    const firstPage = outputEditorState.pages[0];
    const newId = `obj_${Math.random().toString(36).substr(2, 9)}`;
    const newObject = {
      object_id: newId,
      object_type: 'freeform_text',
      page_id: firstPage.page_id,
      x: 100,
      y: 100,
      width: 200,
      height: 30,
      font_size: 12,
      font_family: 'Helvetica',
      value: text || 'New text',
      source_type: 'user_added',
      include: true,
    };

    console.log('[addFreeformText] Adding custom overlay:', {
      id: newId,
      value: newObject.value,
      page: firstPage.page_id,
    });

    setOutputEditorState(prev => {
      const updated = {
        ...prev,
        output_objects: [...(prev?.output_objects || []), newObject],
      };
      console.log('[addFreeformText] State updated. Total objects now:', updated.output_objects.length);
      return updated;
    });

    setSelectedObjectId(newId);
  }, [outputEditorState]);

  // Add checkbox
  const addCheckbox = useCallback((label) => {
    if (!outputEditorState?.pages || outputEditorState.pages.length === 0) return;

    const firstPage = outputEditorState.pages[0];
    const newId = `obj_${Math.random().toString(36).substr(2, 9)}`;
    const newObject = {
      object_id: newId,
      object_type: 'checkbox',
      page_id: firstPage.page_id,
      x: 100,
      y: 100,
      width: 20,
      height: 20,
      checked: false,
      label: label || 'Checkbox',
      source_type: 'user_added',
      include: true,
    };

    setOutputEditorState(prev => ({
      ...prev,
      output_objects: [...(prev?.output_objects || []), newObject],
    }));
    setSelectedObjectId(newId);
  }, [outputEditorState]);

  // Delete a page
  const deletePage = useCallback((pageId) => {
    if (window.confirm('Delete this page? This cannot be undone.')) {
      setOutputEditorState(prev => ({
        ...prev,
        pages: prev.pages.filter(p => p.page_id !== pageId),
        output_objects: prev.output_objects.filter(obj => obj.page_id !== pageId),
      }));
    }
  }, []);

  // Reorder pages
  const reorderPages = useCallback((newPageOrder) => {
    setOutputEditorState(prev => ({
      ...prev,
      pages: newPageOrder.map((pageId, index) => {
        const page = prev.pages.find(p => p.page_id === pageId);
        return { ...page, page_num: index + 1 };
      }),
    }));
  }, []);

  // Get object at screen position
  const getObjectAtPoint = useCallback((pageId, sx, sy) => {
    const pageHeight = pageHeights[pageId] || DEFAULT_PAGE_HEIGHT;
    const pageObjects = outputEditorState?.output_objects?.filter(obj => obj.page_id === pageId) || [];

    for (let i = pageObjects.length - 1; i >= 0; i--) {
      const obj = pageObjects[i];
      const objSx = obj.x * zoom;
      const objSy = (pageHeight - (obj.y + obj.height)) * zoom;
      const objSw = obj.width * zoom;
      const objSh = obj.height * zoom;

      if (sx >= objSx && sx < objSx + objSw && sy >= objSy && sy < objSy + objSh) {
        return obj;
      }
    }
    return null;
  }, [outputEditorState?.output_objects, pageHeights, zoom]);

  // Check if on resize handle
  const isOnResizeHandle = useCallback((obj, sx, sy) => {
    const pageId = obj.page_id;
    const pageHeight = pageHeights[pageId] || DEFAULT_PAGE_HEIGHT;
    const handleSize = 8;
    const objSx = obj.x * zoom;
    const objSy = (pageHeight - (obj.y + obj.height)) * zoom;
    const objSw = obj.width * zoom;
    const objSh = obj.height * zoom;

    const handleLeft = objSx + objSw - handleSize;
    const handleTop = objSy + objSh - handleSize;

    return sx >= handleLeft && sx < objSx + objSw && sy >= handleTop && sy < objSy + objSh;
  }, [pageHeights, zoom]);

  // Canvas event handlers
  const makeCanvasMouseDown = (pageId) => (e) => {
    const refs = pageRefsMap.current[pageId];
    if (!refs?.canvasRef) return;

    const rect = refs.canvasRef.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;

    const obj = getObjectAtPoint(pageId, sx, sy);

    if (obj) {
      if (isOnResizeHandle(obj, sx, sy)) {
        setResizingObjectId(obj.object_id);
        setSelectedObjectId(obj.object_id);
        dragStateRef.current = { sx, sy, startWidth: obj.width, startHeight: obj.height };
      } else {
        setSelectedObjectId(obj.object_id);
        setDraggedObjectId(obj.object_id);
        dragStateRef.current = { sx, sy, startX: obj.x, startY: obj.y };
      }
    } else {
      setSelectedObjectId(null);
    }
  };

  const makeCanvasMouseMove = (pageId) => (e) => {
    const refs = pageRefsMap.current[pageId];
    if (!refs?.canvasRef) return;

    const rect = refs.canvasRef.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;

    if (draggedObjectId && dragStateRef.current) {
      const dx = (sx - dragStateRef.current.sx) / zoom;
      const dy = (sy - dragStateRef.current.sy) / zoom;

      updateObject(draggedObjectId, {
        x: dragStateRef.current.startX + dx,
        y: dragStateRef.current.startY - dy,
      });
    }

    if (resizingObjectId && dragStateRef.current) {
      const dw = (sx - dragStateRef.current.sx) / zoom;
      const dh = (sy - dragStateRef.current.sy) / zoom;

      updateObject(resizingObjectId, {
        width: Math.max(20, dragStateRef.current.startWidth + dw),
        height: Math.max(15, dragStateRef.current.startHeight - dh),
      });
    }

    const obj = getObjectAtPoint(pageId, sx, sy);
    if (obj && isOnResizeHandle(obj, sx, sy)) {
      refs.canvasRef.style.cursor = 'nwse-resize';
    } else if (obj) {
      refs.canvasRef.style.cursor = 'move';
    } else {
      refs.canvasRef.style.cursor = 'default';
    }
  };

  const handleCanvasMouseUp = () => {
    setDraggedObjectId(null);
    setResizingObjectId(null);
    dragStateRef.current = null;
  };

  // Finalize and export
  const handleFinalizeAndExport = async () => {
    if (!outputEditorState) return;

    setIsFinalizing(true);
    try {
      console.log('[ContractOutputEditor] Starting finalize...');

      // CRITICAL: Log what we're about to send
      const allObjects = outputEditorState.output_objects || [];
      const userAdded = allObjects.filter(o => o.source_type === 'user_added');
      const templateFields = allObjects.filter(o => o.source_type === 'template_field');

      console.log('[ContractOutputEditor] ABOUT TO EXPORT:');
      console.log(`  Total objects: ${allObjects.length}`);
      console.log(`  Template fields: ${templateFields.length}`);
      console.log(`  User-added: ${userAdded.length}`);
      if (userAdded.length > 0) {
        console.log(`  User-added objects:`);
        userAdded.forEach(obj => {
          console.log(`    - ${obj.object_id}: ${obj.value || '[no value]'}`);
        });
      }

      // Create the export
      console.log('[ContractOutputEditor] Sending finalize request to:', `${API_URL}/deals/${dealId}/finalize-contract-export`);

      const response = await axios.post(
        `${API_URL}/deals/${dealId}/finalize-contract-export`,
        {
          output_editor_state: outputEditorState,
          // Don't send edited_output_snapshot twice - backend will use output_editor_state for both
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 300000  // 5 minutes timeout for large payload
        }
      );

      console.log('[ContractOutputEditor] Finalize response:', response.status, response.data);
      const exportId = response.data.export_id;

      console.log('[ContractOutputEditor] Fetching PDF for export:', exportId);

      // Fetch the PDF to display in preview modal
      const pdfResponse = await axios.get(
        `${API_URL}/contract-exports/${exportId}/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 60000  // 1 minute timeout for PDF
        }
      );

      console.log('[ContractOutputEditor] PDF fetched:', pdfResponse.data.size, 'bytes');

      const pdfBlob = pdfResponse.data;
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPreviewPDF({ url: pdfUrl, blob: pdfBlob });
      setShowPreviewModal(true);
      console.log('[ContractOutputEditor] Modal shown');
    } catch (err) {
      console.error('[ContractOutputEditor] Error:', {
        message: err.message,
        code: err.code,
        response: err.response ? { status: err.response.status, data: err.response.data } : null,
        config: err.config ? { url: err.config.url, method: err.config.method } : null,
        stack: err.stack
      });
      alert(`Export failed: ${err.message}${err.response ? ` (${err.response.status})` : ''}`);
    } finally {
      setIsFinalizing(false);
    }
  };

  // Handle print from preview modal
  const handlePrintPDF = () => {
    if (!previewPDF) return;
    const printWindow = window.open(previewPDF.url);
    printWindow.addEventListener('load', () => {
      printWindow.print();
    });
  };

  // Handle download from preview modal
  const handleDownloadPDF = () => {
    if (!previewPDF) return;
    const link = document.createElement('a');
    link.href = previewPDF.url;
    link.download = `contract_export_${new Date().getTime()}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Close preview modal and go back to deal
  const handleClosePreview = () => {
    setShowPreviewModal(false);
    if (previewPDF) {
      URL.revokeObjectURL(previewPDF.url);
      setPreviewPDF(null);
    }
    navigate(`/deals/${dealId}`);
  };

  // Handle back
  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return <div style={styles.container}><p>Loading contract editor...</p></div>;
  }

  if (error) {
    return (
      <div style={styles.container}>
        <p style={{ color: COLORS.ERROR }}>Error: {error}</p>
        <button onClick={handleBack}>Go Back</button>
      </div>
    );
  }

  if (!outputEditorState) {
    return <div style={styles.container}><p>No contract to edit</p></div>;
  }

  return (
    <div style={styles.container}>
      {/* PDF Preview Modal */}
      {showPreviewModal && previewPDF && (
        <div style={styles.modalOverlay} onClick={handleClosePreview}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Export Preview</h2>
              <button onClick={handleClosePreview} style={styles.closeBtn}>✕</button>
            </div>
            <div style={styles.modalContent}>
              <iframe
                src={previewPDF.url}
                style={styles.pdfViewer}
                title="PDF Preview"
              />
            </div>
            <div style={styles.modalFooter}>
              <button onClick={handlePrintPDF} style={styles.modalBtn}>
                🖨 Print
              </button>
              <button onClick={handleDownloadPDF} style={styles.modalBtn}>
                💾 Save as PDF
              </button>
              <button onClick={handleClosePreview} style={styles.modalBtnSecondary}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Top frozen bar */}
      <div style={styles.topBar}>
        <button onClick={handleBack} style={styles.backBtn}>
          ← Back
        </button>
        <h1 style={styles.title}>Contract Editor</h1>
        <div style={styles.topBarActions}>
          <button onClick={handleFinalizeAndExport} disabled={isFinalizing} style={styles.exportBtn}>
            {isFinalizing ? 'Exporting...' : 'Finalize & Export'}
          </button>
        </div>
      </div>

      {/* Second toolbar */}
      <div style={styles.toolBar}>
        <div style={styles.zoomControls}>
          <button onClick={() => setZoom(z => Math.max(0.25, z - 0.1))} style={styles.zoomBtn}>−</button>
          <span style={styles.zoomValue}>{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => Math.min(3, z + 0.1))} style={styles.zoomBtn}>+</button>
        </div>
      </div>

      {/* Main content - long scrollable document */}
      <div style={styles.mainContent}>
        {/* Left: Page list + canvas area */}
        <div style={styles.editorArea}>
          <OutputPageThumbnails
            pages={outputEditorState.pages || []}
            currentPageNum={null}
            onSelectPage={() => {}}
            onReorderPages={reorderPages}
            onDeletePage={deletePage}
          />

          <div style={styles.canvasContainer} ref={containerRef}>
            {/* Render all pages as one long stack */}
            {outputEditorState.pages?.map((pageData) => {
              const pageId = pageData.page_id;

              return (
                <div key={pageId} style={styles.pageWrapper}>
                  <canvas
                    ref={el => {
                      if (!pageRefsMap.current[pageId]) {
                        pageRefsMap.current[pageId] = {};
                      }
                      pageRefsMap.current[pageId].canvasRef = el;
                    }}
                    style={styles.pageCanvas}
                  />
                  <canvas
                    ref={el => {
                      if (!pageRefsMap.current[pageId]) {
                        pageRefsMap.current[pageId] = {};
                      }
                      pageRefsMap.current[pageId].overlayCanvasRef = el;
                    }}
                    style={styles.overlayCanvas}
                    onMouseDown={makeCanvasMouseDown(pageId)}
                    onMouseMove={makeCanvasMouseMove(pageId)}
                    onMouseUp={handleCanvasMouseUp}
                  />
                  <div style={styles.pageLabel}>Page {pageData.page_num}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Properties panel */}
        <ContractOutputEditorRightPanel
          selectedObject={
            outputEditorState.output_objects?.find(o => o.object_id === selectedObjectId)
          }
          onUpdateObject={updateObject}
          onDeleteObject={deleteObject}
          onDuplicateObject={duplicateObject}
          onAddFreeformText={addFreeformText}
          onAddCheckbox={addCheckbox}
          warnings={outputEditorState.warnings || []}
          zoom={zoom}
          onZoomChange={setZoom}
        />
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    backgroundColor: COLORS.BG_PRIMARY,
    overflow: 'hidden',
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${SPACING.SM} ${SPACING.LG}`,
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
    boxShadow: SHADOWS.DEFAULT,
    flexShrink: 0,
  },
  backBtn: {
    padding: `${SPACING.XS} ${SPACING.MD}`,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
    color: COLORS.TEXT_PRIMARY,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.SM,
    cursor: 'pointer',
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontWeight: '600',
    transition: 'all 0.2s ease',
  },
  title: {
    ...TYPOGRAPHY.H3,
    margin: 0,
    flex: 1,
    textAlign: 'center',
    color: COLORS.TEXT_PRIMARY,
  },
  topBarActions: {
    display: 'flex',
    gap: SPACING.MD,
  },
  exportBtn: {
    padding: `${SPACING.XS} ${SPACING.LG}`,
    backgroundColor: COLORS.PRIMARY_DARK,
    color: 'white',
    border: 'none',
    borderRadius: RADIUS.SM,
    cursor: 'pointer',
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontWeight: '600',
    transition: 'all 0.2s ease',
  },
  toolBar: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: `${SPACING.SM} ${SPACING.LG}`,
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
    flexShrink: 0,
    gap: SPACING.LG,
  },
  zoomControls: {
    display: 'flex',
    gap: SPACING.SM,
    alignItems: 'center',
  },
  zoomBtn: {
    padding: `${SPACING.XS} ${SPACING.MD}`,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
    color: COLORS.TEXT_PRIMARY,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.SM,
    cursor: 'pointer',
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    fontWeight: '600',
  },
  zoomValue: {
    ...TYPOGRAPHY.BODY_SM,
    color: COLORS.TEXT_PRIMARY,
    minWidth: '60px',
    textAlign: 'center',
  },
  mainContent: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  editorArea: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
  },
  canvasContainer: {
    flex: 1,
    overflow: 'auto',
    padding: SPACING.LG,
    backgroundColor: COLORS.BG_SECONDARY,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  pageWrapper: {
    position: 'relative',
    backgroundColor: 'white',
    boxShadow: SHADOWS.DEFAULT,
    marginBottom: SPACING.LG,
  },
  pageLabel: {
    position: 'absolute',
    bottom: -30,
    left: SPACING.MD,
    ...TYPOGRAPHY.CAPTION,
    color: COLORS.TEXT_SECONDARY,
  },
  pageCanvas: {
    display: 'block',
  },
  overlayCanvas: {
    position: 'absolute',
    top: 0,
    left: 0,
    cursor: 'pointer',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: RADIUS.LG,
    boxShadow: SHADOWS.ELEVATED,
    width: '90vw',
    maxWidth: '900px',
    height: '90vh',
    display: 'flex',
    flexDirection: 'column',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: SPACING.LG,
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
  },
  modalTitle: {
    ...TYPOGRAPHY.H3,
    margin: 0,
    color: COLORS.TEXT_PRIMARY,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: COLORS.TEXT_SECONDARY,
    padding: 0,
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalContent: {
    flex: 1,
    overflow: 'auto',
    backgroundColor: '#f5f5f5',
  },
  pdfViewer: {
    width: '100%',
    height: '100%',
    border: 'none',
  },
  modalFooter: {
    display: 'flex',
    gap: SPACING.MD,
    padding: SPACING.LG,
    borderTop: `1px solid ${COLORS.BORDER_LIGHT}`,
    justifyContent: 'flex-end',
  },
  modalBtn: {
    padding: `${SPACING.SM} ${SPACING.LG}`,
    backgroundColor: COLORS.PRIMARY_DARK,
    color: COLORS.TEXT_WHITE,
    border: 'none',
    borderRadius: RADIUS.SM,
    cursor: 'pointer',
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontWeight: '600',
    transition: 'all 0.2s ease',
  },
  modalBtnSecondary: {
    padding: `${SPACING.SM} ${SPACING.LG}`,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
    color: COLORS.TEXT_PRIMARY,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.SM,
    cursor: 'pointer',
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontWeight: '600',
    transition: 'all 0.2s ease',
  },
};
